import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';
import EPageHeader from '@/components/everridge/EPageHeader';
import ESearchInput from '@/components/everridge/ESearchInput';
import ETableWithApiPagination from '@/components/everridge/table/ETableWithApiPagination';
import { Button } from '@/components/ui/button';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { Roles } from '@/@types/auth.types';
import { useSupportEmailRequestsListApi } from './hooks/useSupportEmailRequestsListApi';
import { useSupportEmailRequestsPageColumns } from './components/SupportEmailRequestsPageColumns';
import type {
    SupportEmailRequestCategory,
    SupportEmailRequestListFilters,
    SupportEmailRequestListItemDto,
    SupportEmailRequestStatus,
} from './types/support-email-request.types';
const SupportEmailRequestsListPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const userRole = useUserRole();
    const columns = useSupportEmailRequestsPageColumns();

    const [page, setPage] = useState(0);
    const [take] = useState(10);
    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('any');
    const [categoryFilter, setCategoryFilter] = useState('any');
    const [suspiciousFilter, setSuspiciousFilter] = useState('any');

    const filters = useMemo<SupportEmailRequestListFilters>(() => {
        const result: SupportEmailRequestListFilters = {};
        if (statusFilter !== 'any')
            result.status = Number(statusFilter) as SupportEmailRequestStatus;
        if (categoryFilter !== 'any')
            result.category = Number(categoryFilter) as SupportEmailRequestCategory;
        if (suspiciousFilter === 'yes') result.isSuspicious = true;
        if (suspiciousFilter === 'no') result.isSuspicious = false;
        return result;
    }, [statusFilter, categoryFilter, suspiciousFilter]);

    const { list, isLoading } = useSupportEmailRequestsListApi({
        searchText: debouncedSearchText,
        pagination: { skip: page * take, take },
        filters,
    });

    useEffect(() => {
        const handler = setTimeout(() => setDebouncedSearchText(searchText), 500);
        return () => clearTimeout(handler);
    }, [searchText]);

    if (userRole !== Roles.superAdmin) return null;

    const handleRowClick = (row: SupportEmailRequestListItemDto) => {
        navigate(`/superAdmin/support-requests/${row.id}`);
    };

    const handleCreate = () => navigate('/superAdmin/support-requests/new');

    return (
        <section className="flex flex-1 flex-col gap-6">
            <EPageHeader
                title={t('supportEmailRequests.title')}
                search={
                    <ESearchInput
                        value={searchText}
                        setValue={(text) => {
                            setPage(0);
                            setSearchText(text);
                        }}
                        placeholder={t('main.superAdmin.tenants.form.search.placeholder')}
                    />
                }
            >
                <Button onClick={handleCreate} className="gap-2">
                    <Mail className="size-4" />
                    {t('supportEmailRequests.add')}
                </Button>
            </EPageHeader>

            <div className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-1.5 text-sm text-gray">
                    <span>{t('supportEmailRequests.filters.status')}</span>
                    <select
                        value={statusFilter}
                        onChange={(e) => {
                            setPage(0);
                            setStatusFilter(e.target.value);
                        }}
                        className="rounded-lg border border-lightgray bg-base px-2 py-1.5 text-sm text-black"
                    >
                        <option value="any">{t('supportEmailRequests.filters.any')}</option>
                        <option value="0">{t('supportEmailRequests.status.new')}</option>
                        <option value="1">{t('supportEmailRequests.status.ongoing')}</option>
                        <option value="2">{t('supportEmailRequests.status.done')}</option>
                        <option value="3">{t('supportEmailRequests.status.cancelled')}</option>
                        <option value="4">{t('supportEmailRequests.status.notRelevant')}</option>
                    </select>
                </label>
                <label className="flex items-center gap-1.5 text-sm text-gray">
                    <span>{t('supportEmailRequests.filters.category')}</span>
                    <select
                        value={categoryFilter}
                        onChange={(e) => {
                            setPage(0);
                            setCategoryFilter(e.target.value);
                        }}
                        className="rounded-lg border border-lightgray bg-base px-2 py-1.5 text-sm text-black"
                    >
                        <option value="any">{t('supportEmailRequests.filters.any')}</option>
                        <option value="0">{t('supportEmailRequests.category.support')}</option>
                        <option value="1">{t('supportEmailRequests.category.privacy')}</option>
                        <option value="2">{t('supportEmailRequests.category.general')}</option>
                        <option value="3">{t('supportEmailRequests.category.unknown')}</option>
                    </select>
                </label>
                <label className="flex items-center gap-1.5 text-sm text-gray">
                    <span>{t('supportEmailRequests.filters.suspicious')}</span>
                    <select
                        value={suspiciousFilter}
                        onChange={(e) => {
                            setPage(0);
                            setSuspiciousFilter(e.target.value);
                        }}
                        className="rounded-lg border border-lightgray bg-base px-2 py-1.5 text-sm text-black"
                    >
                        <option value="any">{t('supportEmailRequests.filters.any')}</option>
                        <option value="yes">{t('supportEmailRequests.filters.suspiciousYes')}</option>
                        <option value="no">{t('supportEmailRequests.filters.suspiciousNo')}</option>
                    </select>
                </label>
            </div>

            <ETableWithApiPagination
                columns={columns}
                data={list}
                paginationStep={take}
                page={page}
                setPage={setPage}
                isLoadingPage={isLoading}
                onRowClick={handleRowClick}
            />
        </section>
    );
};

export default SupportEmailRequestsListPage;
